import {
	ContentExpander,
	Grid,
	Label,
	NumberField,
	Radio,
	Spacer
} from '@components';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
	AllRateCardFuelLevyModifiers, AllRateCardSettingFuelLevyModifiers,
	RateCardFormErrors,
	RateCardSettingsTypes, RateCardTypes
} from '@state';
import {
	RateCardType,
	TRateCardModifierSet,
	TRateCardModifierType,
	TRateCardTailgateLiftModifier,
} from '@types';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { useRecoilValue } from 'recoil';
import styles from './Modifiers.module.scss';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	id?: string;
	isSettings?: boolean;
	type: RateCardType;
	isDisabled?: boolean;
}

export const TailgateLiftModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		id
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		id,
		isSettings: !!isSettings,
		type
	});
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateTailgateModifier = (
		key: keyof TRateCardTailgateLiftModifier,
		newValue: string | TRateCardModifierType
	) => {
		const update = {
			...modifiers,
			tailgate_lift: newValue === 'None' ? null : {
				...modifiers?.tailgate_lift,
				...(key === 'type' ? {
					value: '',
					max_charge: '',
					pallets_per_lift: ''
				} : {}),
				[key]: String(newValue),
				attracts_fuel_levy: !!fuelLevyModifiers?.tailgate_lift?.attracts_fuel_levy
			}
		} as TRateCardModifierSet;

		if (key === 'type' && newValue === 'PER_STOP') {
			delete update.tailgate_lift?.pallets_per_lift;
			delete update.tailgate_lift?.max_charge;
		}

		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}

		setRateCardModifiers(newModifiers);
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex].modifier_set.tailgate_lift[key]) {
			clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.tailgate_lift.${key}`)
		}
	}

	const handleOnBlur = (
		modifierKey: keyof TRateCardTailgateLiftModifier,
		newValue: string
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateTailgateModifier(modifierKey, value);
		}
		if (newValue.startsWith('.')) {
			updateTailgateModifier(modifierKey, `0${newValue}`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.tailgate_lift?.[key]?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = ['max_charge', 'value', 'pallets_per_lift']
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	const getConfiguredCount = () => {
		if (!modifiers?.tailgate_lift) return 0;
		return parseFloat(`${modifiers.tailgate_lift.value ?? ''}`) > 0 ? 1 : 0;
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Tailgate lift
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Label>Charge method</Label>
				<Radio.Group className={styles.radioContainer}>
					<Radio
						name={'charge_method'}
						label={'Fee per stop'}
						value={TRateCardModifierType.PER_STOP}
						onChange={(e) => updateTailgateModifier('type', e.target.value)}
						checked={modifiers?.tailgate_lift?.type === TRateCardModifierType.PER_STOP}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'Per lift'}
						value={TRateCardModifierType.PER_LIFT}
						onChange={(e) => updateTailgateModifier('type', e.target.value)}
						checked={modifiers?.tailgate_lift?.type === TRateCardModifierType.PER_LIFT}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'None'}
						value={'None'}
						onChange={(e) => updateTailgateModifier('type', e.target.value)}
						checked={modifiers?.tailgate_lift === null}
						disabled={isDisabled}
					/>
				</Radio.Group>
				{
					modifiers?.tailgate_lift?.type &&
						<Grid columns={3} columnTemplate={'repeat(3, 1fr)'} gap={'16px'} alignItems='start'>
							{
								modifiers?.tailgate_lift?.type === TRateCardModifierType.PER_STOP &&
									<NumberField
										name={'fee_per_stop'}
										value={`${modifiers.tailgate_lift?.value ?? ''}`}
										onChange={(e) => updateTailgateModifier('value', e.target.value)}
										leftModule={<span className={styles.prefix}>$</span>}
										label={'Fee per stop'}
										keepErrorSpace={false}
										required
										hasError={!!hasError('value')}
										errorMessage={hasError('value')}
										disabled={isDisabled}
										min={'0'}
										onBlur={(e) => handleOnBlur('value', e.target.value)}
									/>
							}
							{
								modifiers?.tailgate_lift?.type === TRateCardModifierType.PER_LIFT &&
									<>
										<NumberField
											name={'value'}
											value={`${modifiers.tailgate_lift?.value ?? ''}`}
											onChange={(e) => updateTailgateModifier('value', e.target.value)}
											leftModule={<span className={styles.prefix}>$</span>}
											label={'Fee per lift'}
											keepErrorSpace={false}
											required
											hasError={!!hasError('value')}
											errorMessage={hasError('value')}
											disabled={isDisabled}
											min={'0'}
											onBlur={(e) => handleOnBlur('value', e.target.value)}
										/>
										<NumberField
											name={'pallets_per_lift'}
											value={`${modifiers.tailgate_lift?.pallets_per_lift ?? ''}`}
											onChange={(e) => updateTailgateModifier('pallets_per_lift', e.target.value)}
											leftModule={<span className={styles.prefix}>#</span>}
											label={'Spaces per lift'}
											keepErrorSpace={false}
											required
											hasError={!!hasError('pallets_per_lift')}
											errorMessage={hasError('pallets_per_lift')}
											disabled={isDisabled}
											min={'0'}
											onBlur={(e) => handleOnBlur('pallets_per_lift', e.target.value)}
										/>
										<NumberField
											name={'max_charge'}
											value={`${modifiers.tailgate_lift?.max_charge ?? ''}`}
											onChange={(e) => updateTailgateModifier('max_charge', e.target.value)}
											leftModule={<span className={styles.prefix}>$</span>}
											label={'Max. charge'}
											keepErrorSpace={false}
											hasError={!!hasError('max_charge')}
											errorMessage={hasError('max_charge')}
											disabled={isDisabled}
											min={'0'}
											onBlur={(e) => handleOnBlur('max_charge', e.target.value)}
										/>
									</>
							}
						</Grid>
				}
				<Spacer size={5} />
			</div>

		</ContentExpander>
	);
}